import { Check, ChevronDown } from '@untitled-ui/icons-react/build/cjs';
import parsePhoneNumberFromString, { formatIncompletePhoneNumber, parseIncompletePhoneNumber } from 'libphonenumber-js';
import React, { memo, useEffect, useLayoutEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { getCountries, getCountryCallingCode } from 'react-phone-number-input/input';
import en from 'react-phone-number-input/locale/en.json';
import ErrorMsg from 'src/components/errorMsg';
import InputText from 'src/components/InputText/InputText';
import { MAX_LENGTH, CANADIAN_AREA_CODES } from 'src/constants/common';
import { usePhone } from 'src/hooks/usePhone';
import { getCountryFlag } from 'src/utils/CommonFunctions';

import { INumberList, ISelectedNumber } from './PhoneInput.interface';

interface IProps {
    getNumber?: any;
    onNumberChange: any;
    onBlur?: any;
    disabled?: any;
    errors: any;
    name: any;
    isFocus?: any;
    isButtonClicked?: any;
    phoneInputParentClassName?: any;
    value?: any;
    isShowErrorMsg?: any;
    isPhoneNumberValue?: boolean;
    setIsPhoneNumberValue?: any;
    dropdownDelayClassname?: string;
    phoneInputWrapClassName?: string;
    phoneInputWrapMainClassName?: string;
    label?: string | React.ReactElement | boolean;
    labelClassName?: string;
    required?: boolean;
    requiredClassName?: string;
    onKeyPress?: any;
    phoneInputClassName?: string;
    disableAutoCountryDetection?: boolean;
    maxLength?: number;
}

const PhoneInput = ({
    getNumber,
    label,
    labelClassName,
    onNumberChange,
    disabled,
    errors = false,
    name,
    isFocus = false,
    isButtonClicked,
    phoneInputParentClassName,
    value = '',
    onBlur,
    isShowErrorMsg = true,
    isPhoneNumberValue,
    setIsPhoneNumberValue,
    dropdownDelayClassname = '',
    phoneInputWrapClassName,
    phoneInputWrapMainClassName,
    required,
    requiredClassName,
    onKeyPress,
    phoneInputClassName,
    disableAutoCountryDetection = false,
    maxLength = MAX_LENGTH.PHONE_NUMBER,
}: IProps) => {
    const { t } = useTranslation();
    const { setFormatPhone, isPhoneValid } = usePhone();

    const [searchTerm, setSearchTerm] = React.useState('');
    const phoneNumber = value ? parsePhoneNumberFromString(value) : null;

    // Restrict +1 countries to only US and CA, always use area code detection
    const restrictToUSOrCA = (country: string | undefined, nationalNumber: string | undefined, code: string): 'CA' | 'US' => {
        // Only apply restriction for +1 country code
        if (code !== '1') {
            return (country as 'CA' | 'US') || 'CA';
        }

        // Always use area code detection for +1 numbers to determine US or CA
        if (nationalNumber && nationalNumber.length >= 3) {
            const areaCode = nationalNumber.substring(0, 3);

            if (CANADIAN_AREA_CODES.includes(areaCode)) {
                return 'CA';
            }

            // If area code is not in Canadian list, it's US
            return 'US';
        }

        // Default to US for +1 if we can't determine (not enough digits)
        return 'US';
    };

    // Cursor position tracking
    const inputRef = useRef<HTMLInputElement>(null);
    const [cursorPosition, setCursorPosition] = React.useState<number | null>(null);
    const [shouldRestoreCursor, setShouldRestoreCursor] = React.useState(false);

    const restrictedCountry = restrictToUSOrCA(phoneNumber?.country, phoneNumber?.nationalNumber, phoneNumber?.countryCallingCode ?? '1');

    const initialSelectedNumber: ISelectedNumber = {
        code: phoneNumber?.countryCallingCode ?? '1',
        number: phoneNumber?.nationalNumber ?? '',
        image: getCountryFlag(restrictedCountry) || restrictedCountry.toUpperCase(),
        name: en[restrictedCountry],
        country: restrictedCountry,
    };

    const [Switch, setSwitch] = React.useState(false);
    const [selectedNumber, setSelectedNumber] = React.useState<ISelectedNumber>(initialSelectedNumber);
    const [numberList, setNumberList] = React.useState<INumberList[]>([]);
    const [isInit, setIsInit] = React.useState(true);

    const dropdownRef = React.useRef<HTMLDivElement>(null);
    const triggerRef = React.useRef<HTMLSpanElement>(null);

    useEffect(() => {
        if (phoneNumber && isPhoneNumberValue) {
            const normalizedCountry = restrictToUSOrCA(phoneNumber?.country, phoneNumber?.nationalNumber, phoneNumber?.countryCallingCode ?? '1');
            setSelectedNumber({
                code: phoneNumber?.countryCallingCode ?? '1',
                number: phoneNumber?.nationalNumber ?? '',
                image: getCountryFlag(normalizedCountry) || normalizedCountry.toUpperCase(),
                name: en[normalizedCountry],
                country: normalizedCountry,
            });
            setIsPhoneNumberValue(false);
        }

        if (!phoneNumber?.nationalNumber && isPhoneNumberValue) {
            const normalizedCountry = restrictToUSOrCA(phoneNumber?.country, phoneNumber?.nationalNumber, phoneNumber?.countryCallingCode ?? '1');
            setSelectedNumber({
                code: phoneNumber?.countryCallingCode ?? '1',
                number: phoneNumber?.nationalNumber ?? '',
                image: getCountryFlag(normalizedCountry) || normalizedCountry.toUpperCase(),
                name: en[normalizedCountry],
                country: normalizedCountry,
            });
            setIsPhoneNumberValue(false);
        }
    }, [phoneNumber, isPhoneNumberValue]);

    // Update country based on area code when value changes (for edit mode)
    useEffect(() => {
        if (phoneNumber && phoneNumber.countryCallingCode === '1' && phoneNumber.nationalNumber) {
            const normalizedCountry = restrictToUSOrCA(phoneNumber.country, phoneNumber.nationalNumber, '1');

            setSelectedNumber((prev) => {
                // Only update if the country has changed
                if (prev.country !== normalizedCountry) {
                    return {
                        ...prev,
                        country: normalizedCountry,
                        image: getCountryFlag(normalizedCountry) || normalizedCountry.toUpperCase(),
                        name: en[normalizedCountry],
                    };
                }

                return prev;
            });
        }
    }, [value]);

    useEffect(() => {
        const handler = (e: any) => {
            if (Switch && dropdownRef.current && !dropdownRef.current.contains(e.target) && triggerRef.current && !triggerRef.current.contains(e.target)) {
                setSwitch(false);
            }
        };
        window.addEventListener('mousedown', handler);

        return () => {
            window.removeEventListener('mousedown', handler);
        };
    }, [Switch]);

    const handlerInputClick = (e: any) => {
        e.stopPropagation();
        e.preventDefault();

        if (!Switch) {
            setSearchTerm('');
        }
        setSwitch(!Switch);
    };

    const handleCountryCode = (option: ISelectedNumber | INumberList) => {
        setSelectedNumber({ ...selectedNumber, ...option });
        setSwitch(false);
    };

    const detectCountryByAreaCode = (inputPhoneNumber: string): 'CA' | 'US' | null => {
        if (selectedNumber.code !== '1') {
            return null;
        }

        const digitsOnly = inputPhoneNumber.replace(/\D/g, '');

        if (digitsOnly.length < 3) {
            return null;
        }

        const areaCode = digitsOnly.substring(0, 3);

        if (CANADIAN_AREA_CODES.includes(areaCode)) {
            return 'CA';
        } else {
            return 'US';
        }
    };

    const findCursorPositionAfterDigits = (formattedValue: string, digitCount: number): number => {
        let currentDigits = 0;

        for (let i = 0; i < formattedValue.length; i++) {
            if (/\d/.test(formattedValue[i])) {
                currentDigits++;

                if (currentDigits === digitCount) {
                    return i + 1; // Position after this digit
                }
            }
        }

        return formattedValue.length; // If not enough digits, go to end
    };

    // Handle keydown for deletion of formatting characters
    const handleKeyDown = (e: any) => {
        // Call the original onKeyPress if it exists
        if (onKeyPress) {
            onKeyPress(e);
        }

        const input = e.target;
        const cursorPos = input.selectionStart || 0;
        const currentValue = input.value;

        // Helper function for both backspace and delete
        const handleFormattingCharacterDeletion = (isBackspace: boolean): boolean => {
            const targetChar = isBackspace ? currentValue[cursorPos - 1] : currentValue[cursorPos];

            if (!targetChar || /\d/.test(targetChar)) return false;

            e.preventDefault();

            // Find the nearest digit in the appropriate direction
            let digitPos = isBackspace ? cursorPos - 1 : cursorPos + 1;
            const step = isBackspace ? -1 : 1;
            const limit = isBackspace ? 0 : currentValue.length;

            while (digitPos !== limit && /[^\d]/.test(currentValue[digitPos])) {
                digitPos += step;
            }

            // If we found a digit, remove it
            if ((isBackspace && digitPos >= 0) || (!isBackspace && digitPos < currentValue.length)) {
                const digitsOnly = currentValue.replace(/\D/g, '');
                const digitIndex = currentValue.slice(0, digitPos).replace(/\D/g, '').length;
                const newDigits = digitsOnly.slice(0, digitIndex) + digitsOnly.slice(digitIndex + 1);

                setSelectedNumber({ ...selectedNumber, number: newDigits });

                const newFormattedValue = setFormatPhone(selectedNumber.code, newDigits, selectedNumber.country, maxLength);

                const newCursorPos = findCursorPositionAfterDigits(newFormattedValue, isBackspace ? digitIndex : digitIndex);

                setCursorPosition(newCursorPos);
                setShouldRestoreCursor(true);
            }

            return true;
        };

        // Handle backspace on formatting characters
        if (e.key === 'Backspace' && cursorPos > 0) {
            handleFormattingCharacterDeletion(true);
        }

        // Handle delete key on formatting characters
        if (e.key === 'Delete' && cursorPos < currentValue.length) {
            handleFormattingCharacterDeletion(false);
        }
    };

    const handleInputNumberChange = (e: any) => {
        const input = e.target;
        const currentCursorPos = input.selectionStart || 0;
        const inputValue = e.target.value;

        // Only perform auto-detection if not disabled
        if (!disableAutoCountryDetection) {
            const fullPhoneNumber = parsePhoneNumberFromString(inputValue);

            if (fullPhoneNumber && fullPhoneNumber.countryCallingCode) {
                const detectedCode = fullPhoneNumber.countryCallingCode;
                const detectedNationalNumber = fullPhoneNumber.nationalNumber || '';

                // Restrict +1 countries to only US and CA
                const detectedCountry = restrictToUSOrCA(fullPhoneNumber.country, detectedNationalNumber, detectedCode);

                if (detectedCountry && (detectedCountry !== selectedNumber.country || detectedCode !== selectedNumber.code)) {
                    const newSelectedNumber = {
                        code: detectedCode,
                        number: detectedNationalNumber,
                        image: getCountryFlag(detectedCountry) || detectedCountry.toUpperCase(),
                        name: en[detectedCountry],
                        country: detectedCountry,
                    };

                    const newFormattedValue = setFormatPhone(detectedCode, detectedNationalNumber, detectedCountry, maxLength);

                    const digitsBeforeCursor = inputValue.slice(0, currentCursorPos).replace(/\D/g, '').length;

                    const newCursorPos = findCursorPositionAfterDigits(newFormattedValue, digitsBeforeCursor);

                    setCursorPosition(newCursorPos);
                    setShouldRestoreCursor(true);
                    setSelectedNumber(newSelectedNumber);

                    return;
                }
            }
        }

        let newValue = parseIncompletePhoneNumber(inputValue);

        if (newValue.startsWith('+')) {
            newValue = newValue.replace(`+${selectedNumber.code}`, '');
        }

        if (newValue === selectedNumber.number) {
            const newValueFormatted = formatIncompletePhoneNumber(newValue, selectedNumber.country);

            if (newValueFormatted.indexOf(inputValue) === 0) {
                newValue = newValue.slice(0, -1);
            }
        }

        // Always perform area code detection for +1 numbers to determine US or CA flag
        // This works even when disableAutoCountryDetection is true
        if (selectedNumber.code === '1' && newValue.length >= 3) {
            const detectedCountryByAreaCode = detectCountryByAreaCode(newValue);

            if (detectedCountryByAreaCode && detectedCountryByAreaCode !== selectedNumber.country) {
                const newSelectedNumber: ISelectedNumber = {
                    code: selectedNumber.code,
                    number: newValue,
                    image: getCountryFlag(detectedCountryByAreaCode) || detectedCountryByAreaCode.toUpperCase(),
                    name: en[detectedCountryByAreaCode as keyof typeof en],
                    country: detectedCountryByAreaCode,
                };

                const newFormattedValue = setFormatPhone(selectedNumber.code, newValue, detectedCountryByAreaCode, maxLength);

                const digitsBeforeCursor = inputValue.slice(0, currentCursorPos).replace(/\D/g, '').length;

                const newCursorPos = findCursorPositionAfterDigits(newFormattedValue, digitsBeforeCursor);

                setCursorPosition(newCursorPos);
                setShouldRestoreCursor(true);
                setSelectedNumber(newSelectedNumber);

                return;
            }
        }

        const newFormattedValue = setFormatPhone(selectedNumber.code, newValue, selectedNumber.country, maxLength);

        // Count digits up to cursor position in the INPUT
        const digitsBeforeCursor = inputValue.slice(0, currentCursorPos).replace(/\D/g, '').length;

        const newCursorPos = findCursorPositionAfterDigits(newFormattedValue, digitsBeforeCursor);

        setCursorPosition(newCursorPos);
        setShouldRestoreCursor(true);
        setSelectedNumber({ ...selectedNumber, number: newValue });
    };

    // Restore cursor position after formatting
    useLayoutEffect(() => {
        if (shouldRestoreCursor && cursorPosition !== null && inputRef.current) {
            inputRef.current.setSelectionRange(cursorPosition, cursorPosition);
            setShouldRestoreCursor(false);
            setCursorPosition(null);
        }
    }, [shouldRestoreCursor, cursorPosition]);

    useEffect(() => {
        if (getNumber) {
            getNumber(`+${selectedNumber.code}${selectedNumber.number}`);
        }

        const isValid = isPhoneValid(selectedNumber.number, selectedNumber.country);
        onNumberChange(selectedNumber.country, selectedNumber.number, selectedNumber.code, isValid);
    }, [selectedNumber.code, selectedNumber.number, selectedNumber.country]);

    const handleInputBlur = () => {
        if (onBlur) {
            const isValid = isPhoneValid(selectedNumber.number, selectedNumber.country);

            onBlur(selectedNumber.country, selectedNumber.number, selectedNumber.code, isValid);
        }
    };

    const handlePaste = (e: any) => {
        e.preventDefault();

        const pastedData = e.clipboardData.getData('text');
        const pastedDigits = pastedData.replace(/\D/g, '');

        const input = e.target as HTMLInputElement;
        const currentCursorPos = input.selectionStart || 0;
        const selectionEnd = input.selectionEnd || currentCursorPos;
        const currentValue = input.value;

        // Count digits that will remain after the selection is replaced
        const digitsBeforeCursor = currentValue.slice(0, currentCursorPos).replace(/\D/g, '').length;
        const digitsAfterSelection = currentValue.slice(selectionEnd).replace(/\D/g, '').length;
        const remainingCapacity = Math.max(0, maxLength - digitsBeforeCursor - digitsAfterSelection);

        const truncatedDigits = pastedDigits.slice(0, remainingCapacity);

        const newValue = currentValue.slice(0, currentCursorPos) + truncatedDigits + currentValue.slice(selectionEnd);

        const syntheticEvent = {
            target: {
                ...input,
                value: newValue,
            },
        };

        handleInputNumberChange(syntheticEvent);
    };

    const [filterList, setFilterList] = React.useState<INumberList[]>([]);
    // (870) 899-7878
    useLayoutEffect(() => {
        const fetchData = async () => {
            let list = getCountries().map((country) => ({
                image: getCountryFlag(country) || country.toUpperCase(),
                code: getCountryCallingCode(country),
                name: en[country],
                country: country,
            }));

            const priorityCountries = ['Canada', 'United States'];

            const customSortValue = (country: string) => {
                const priorityIndex = priorityCountries.indexOf(country);

                return priorityIndex !== -1 ? priorityIndex : priorityCountries.length + 1;
            };

            const shortedList = list.sort((a, b) => customSortValue(a.name) - customSortValue(b.name) || a.name.localeCompare(b.name));

            setNumberList(shortedList);
            setFilterList(shortedList);
        };

        fetchData();
    }, []);

    const handleSearchTermChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const term = e.target.value;
        setSearchTerm(term);
    };

    useEffect(() => {
        if (isInit) {
            setIsInit(false);

            return;
        }
        const searchTermTrimmed = searchTerm?.trim();
        const searchWithoutPlus = searchTermTrimmed?.startsWith('+') ? searchTermTrimmed.substring(1) : searchTermTrimmed;

        const filteredList = numberList.filter((country) => country.name.toLowerCase().includes(searchTermTrimmed?.toLowerCase()) || country.code.includes(searchWithoutPlus));

        if (searchTermTrimmed === '') {
            setFilterList(numberList);
        } else {
            setFilterList(filteredList);
        }
    }, [searchTerm]);

    return (
        <div className={`relative ${phoneInputParentClassName}`}>
            {label !== undefined && (
                <label className={`form_label block mb-1.5 ${labelClassName}`}>
                    {label === true ? t('Common.Phone_Number') : label}
                    {required && <span className={`text-red-600 ms-[2px] leading-4 text-xs ${requiredClassName}`}>*</span>}
                </label>
            )}
            <div className={`relative ${phoneInputWrapMainClassName}`}>
                <div
                    className={`${phoneInputWrapClassName} ${disabled ? 'hover:border-borderPrimary bg-gray50' : ''} form_control flex items-center  lg:py-[7px] py-[7px] ${Switch && ''} ${
                        errors || errors || (!isPhoneValid(selectedNumber.number, selectedNumber.country) && isButtonClicked) ? 'is-invalid' : ''
                    }`}
                >
                    <span className={`flex items-center gap-x-[5px] flex-none ${disabled ? '!cursor-default' : 'cursor-pointer'}`} onClick={handlerInputClick} ref={triggerRef}>
                        {selectedNumber.image}
                        <ChevronDown className="w-4 h-4 text-gray400 flex-none" />
                    </span>
                    <p className="ml-1.5 mr-1">+{selectedNumber.code}</p>
                    <input
                        ref={inputRef}
                        className={`text-xs bg-transparent rounded-r-3xl focus:ring-0 focus:ring-offset-0 focus-visible:outline-none w-full border-none ${phoneInputClassName}`}
                        type="text"
                        name={name}
                        disabled={disabled}
                        value={setFormatPhone(selectedNumber.code, selectedNumber.number, selectedNumber.country, maxLength)}
                        onKeyDown={handleKeyDown}
                        onChange={handleInputNumberChange}
                        onBlur={handleInputBlur}
                        onPaste={handlePaste}
                        placeholder={'(870) 870-8866'}
                        autoFocus={isFocus}
                        maxLength={maxLength}
                    />
                </div>

                {isShowErrorMsg && errors && errors ? <ErrorMsg errorText={errors ?? t('Common.Invalid_phone_number')} /> : ''}
                {!disabled && Switch && (
                    <div ref={dropdownRef} id="dropdownDelay" className={`z-10 absolute shadow-lg bg-white w-full border border-utilityGray200 my-1 rounded-lg ${dropdownDelayClassname}`}>
                        <InputText
                            className="country"
                            placeholder={t('Common.Search_Country')}
                            value={searchTerm}
                            onChangeFunc={handleSearchTermChange}
                            isClearable={true}
                            parentClassName="px-1.5 py-2"
                        />
                        <ul className="country-code-dropdown-list py-1 px-1.5 max-h-[150px] overflow-y-auto scrollbar-hide" aria-labelledby="dropdownDelayButton">
                            {filterList.length > 0 ? (
                                filterList.map((country: any, indexNumberCode: number) => (
                                    <li
                                        onClick={() => handleCountryCode(country)}
                                        className={`country-code-dropdown-li px-2 py-1.5 text-xs rounded-md cursor-pointer [&.active]:!bg-gray50 hover:!bg-gray50 transition-all duration-[0.3s] flex justify-between items-center align-middle ${
                                            selectedNumber.name === country.name && 'active'
                                        }`}
                                        key={indexNumberCode}
                                        value={country.code}
                                    >
                                        <div className="flex items-center">
                                            <span className="font-normal text-grayLight600 mr-2 self-start">{country.image}</span>
                                            <span className="font-normal text-grayLight600 mr-2 self-start">+{country.code}</span>

                                            <span className="font-normal text-grayLight600 mr-2">{country.name}</span>
                                        </div>

                                        {selectedNumber.country === country.country && <Check className="w-4 h-4 text-primary" />}
                                    </li>
                                ))
                            ) : (
                                <li className="px-2 py-1.5 text-xs text-center hover:!bg-gray50">{t('Common.No_results_found')}</li>
                            )}
                        </ul>
                    </div>
                )}
            </div>
        </div>
    );
};

export default memo(PhoneInput);
